import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Skeleton, Spin, Typography, theme } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  EmptyDeskIllustration,
  ReservationCard,
} from '../BookingsList/components/DeskReservationCard';
import {
  ProfileReservation,
  ReservationMenuAction,
} from '../BookingsList/utils/getReservationActions';
import { formatReservationDateTimeLabel } from '../BookingsList/utils/formatBookings';

type DesksListProps = {
  /** Assigned desks (deduped by series), pinned above the flex reservations. */
  assigned: ProfileReservation[];
  /** Flex / hotel reservations, ordered by date across the loaded window. */
  flex: ProfileReservation[];
  recurrenceByDeskId: Record<string, string | null>;
  isCurrentPerson: boolean;
  onCardClick: (reservation: ProfileReservation) => void;
  onCheckIn: (reservation: ProfileReservation) => void;
  onMenuAction: (
    action: ReservationMenuAction,
    reservation: ProfileReservation
  ) => void;
  /** Whether more future flex reservations may be loaded on scroll. */
  hasMore: boolean;
  /** A further window of flex reservations is currently loading. */
  loadingMore: boolean;
  onLoadMore: () => void;
};

export const DesksList = ({
  assigned,
  flex,
  recurrenceByDeskId,
  isCurrentPerson,
  onCardClick,
  onCheckIn,
  onMenuAction,
  hasMore,
  loadingMore,
  onLoadMore,
}: DesksListProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings',
  });
  const { t: tDesks } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'desks',
  });
  const { token } = theme.useToken();
  const todayLabel = t('today');

  const sentinelRef = useRef<HTMLDivElement>(null);
  // Keep the latest onLoadMore/flags in a ref so the observer callback never
  // goes stale without re-subscribing on every render.
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;
  const canLoadMore = hasMore && !loadingMore;
  const canLoadMoreRef = useRef(canLoadMore);
  canLoadMoreRef.current = canLoadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (
        entries.some((entry) => entry.isIntersecting) &&
        canLoadMoreRef.current
      ) {
        loadMoreRef.current();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  if (assigned.length === 0 && flex.length === 0) {
    return (
      <Section>
        <EmptyCard $borderColor={token.colorBorderSecondary}>
          <EmptyDeskIllustration width={140} height={60} />
          <Typography.Text type="secondary">{tDesks('empty')}</Typography.Text>
        </EmptyCard>
      </Section>
    );
  }

  return (
    <Section>
      {assigned.map((reservation) => (
        <ReservationCard
          key={reservation.id}
          reservation={reservation}
          recurrence={recurrenceByDeskId[reservation.seat.id] ?? null}
          isCurrentPerson={isCurrentPerson}
          onCardClick={onCardClick}
          onCheckIn={onCheckIn}
          onMenuAction={onMenuAction}
          wideLayout
        />
      ))}
      {flex.map((reservation) => (
        <ReservationCard
          key={reservation.id}
          reservation={reservation}
          recurrence={null}
          isCurrentPerson={isCurrentPerson}
          onCardClick={onCardClick}
          onCheckIn={onCheckIn}
          onMenuAction={onMenuAction}
          wideLayout
          metaLabel={formatReservationDateTimeLabel(
            reservation.startTime,
            reservation.endTime,
            reservation.timeZone,
            todayLabel
          )}
        />
      ))}
      {/* Sentinel drives infinite scroll; the spinner shows while the next
          window of future reservations loads. */}
      <Sentinel ref={sentinelRef} aria-hidden={!hasMore}>
        {loadingMore && <Spin size="small" />}
      </Sentinel>
    </Section>
  );
};

export const DesksListSkeleton = (): JSX.Element => {
  const { token } = theme.useToken();
  return (
    <Section>
      {[0, 1, 2].map((row) => (
        <EmptyCard
          key={row}
          $borderColor={token.colorBorderSecondary}
          data-testid="desks-card-skeleton"
        >
          <Skeleton active paragraph={{ rows: 2 }} title={false} />
        </EmptyCard>
      ))}
    </Section>
  );
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 16px 16px;
`;

const EmptyCard = styled.div<{ $borderColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
  text-align: center;
`;

const Sentinel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 8px;
`;
